/**
 * Lucky Wheel Component
 */
class LuckyWheel {
    constructor(element) {
      this.element = element;
      this.overlay = element.querySelector('#luckyWheelOverlay');
      this.closeBtn = element.querySelector('#closeWheelPopup');
      this.minimized = element.querySelector('#wheelMinimized');
      this.canvas = element.querySelector('#wheelCanvas');
      this.ctx = this.canvas.getContext('2d');
      this.form = element.querySelector('#luckyWheelForm');
      this.emailInput = element.querySelector('#wheelEmail');
      this.spinButton = element.querySelector('#spinButton');
      this.resultDiv = element.querySelector('#wheelResult');
      this.resultTitle = element.querySelector('#resultTitle');
      this.resultText = element.querySelector('#resultText');
      this.resultCoupon = element.querySelector('#resultCoupon');
      this.couponCode = element.querySelector('#couponCode');
      this.copyButton = element.querySelector('#copyButton');
      this.popup = element.querySelector('.tpt-lucky-wheel_popup');
      this.content = element.querySelector('.tpt-lucky-wheel_content');
      
      // Wheel properties
      this.wheelData = this.loadWheelData();
      this.isSpinning = false;
      this.currentRotation = 0;
      this.wheelSize = parseInt(this.element.style.getPropertyValue('--wheel-size')) || 300;
      
      // Settings
      this.autoShow = this.element.dataset.autoShow === 'true';
      this.popupDelay = parseInt(this.element.dataset.delay) || 2;
      this.originalButtonText = this.spinButton.querySelector('span')?.textContent || 'Spin The Wheel';
      
      this.init();
    }
    
    init() {
      this.setupCanvas();
      this.drawWheel();
      this.bindEvents();
      
      // Check if user has spun today and show result if available
      if (this.hasSpunToday()) {
        const storedResult = this.getStoredResult();
        if (storedResult) {
          this.showMinimized();
          return;
        }
      }
      
      // Auto show popup
      if (this.autoShow && !this.hasSpunToday()) {
        setTimeout(() => {
          this.showPopup();
        }, this.popupDelay * 1000);
      }
    }
    
    setupCanvas() {
      const size = this.wheelSize;
      this.canvas.width = size;
      this.canvas.height = size;
      this.canvas.style.width = size + 'px';
      this.canvas.style.height = size + 'px';
      
      // Scale for high DPI
      const dpr = window.devicePixelRatio || 1;
      this.canvas.width = size * dpr;
      this.canvas.height = size * dpr;
      this.ctx.scale(dpr, dpr);
      
      // Update canvas size based on screen size
      this.updateCanvasSize();
    }
    
    updateCanvasSize() {
      let scaleFactor = 1;
      
      if (window.innerWidth <= 480) { // Extra small mobile
        scaleFactor = 0.55;
      } else if (window.innerWidth <= 768) { // Mobile
        scaleFactor = 0.65;
      } else if (window.innerWidth <= 1024) { // Tablet
        scaleFactor = 0.8;
      }
      
      const scaledSize = this.wheelSize * scaleFactor;
      this.canvas.style.width = scaledSize + 'px';
      this.canvas.style.height = scaledSize + 'px';
    }
    
    loadWheelData() {
      const dataScript = document.getElementById('wheelData');
      if (dataScript) {
        try {
          return JSON.parse(dataScript.textContent);
        } catch (e) {
          console.error('Failed to parse wheel data:', e);
        }
      }
      return [];
    }
    
    drawWheel() {
      if (!this.wheelData.length) return;
      
      const centerX = this.wheelSize / 2;
      const centerY = this.wheelSize / 2;
      const radius = this.wheelSize / 2 - 10;
      const segmentAngle = (2 * Math.PI) / this.wheelData.length;
      
      this.ctx.clearRect(0, 0, this.wheelSize, this.wheelSize);
      
      // Draw segments
      this.wheelData.forEach((segment, index) => {
        const startAngle = index * segmentAngle + this.currentRotation;
        const endAngle = startAngle + segmentAngle;
        
        // Draw segment
        this.ctx.beginPath();
        this.ctx.moveTo(centerX, centerY);
        this.ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        this.ctx.closePath();
        
        // Fill segment
        this.ctx.fillStyle = segment.backgroundColor;
        this.ctx.fill();
        
        // Draw border
        this.ctx.strokeStyle = '#ffffff';
        this.ctx.lineWidth = 2;
        this.ctx.stroke();
        
        // Draw text
        this.ctx.save();
        this.ctx.translate(centerX, centerY);
        this.ctx.rotate(startAngle + segmentAngle / 2);
        this.ctx.fillStyle = segment.textColor;
        
        // Dynamic font size based on text length and wheel size
        const textLength = segment.label.length;
        let fontSize = Math.max(10, Math.min(16, this.wheelSize / 20));
        if (textLength > 8) fontSize = Math.max(8, fontSize - 2);
        if (textLength > 12) fontSize = Math.max(6, fontSize - 2);
        
        this.ctx.font = `bold ${fontSize}px Arial`;
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        
        // Text positioning
        const textRadius = radius * 0.7;
        
        // Handle long text by wrapping
        const words = segment.label.split(' ');
        const maxWidth = radius * 0.6;
        let lines = [];
        let currentLine = '';
        
        words.forEach(word => {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const metrics = this.ctx.measureText(testLine);
          
          if (metrics.width > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        });
        
        if (currentLine) {
          lines.push(currentLine);
        }
        
        // Limit to 2 lines and add ellipsis if needed
        if (lines.length > 2) {
          lines = lines.slice(0, 2);
          lines[1] = lines[1].substring(0, lines[1].length - 3) + '...';
        }
        
        // Draw lines
        const lineHeight = fontSize * 1.2;
        const totalHeight = lines.length * lineHeight;
        const startY = -totalHeight / 2 + lineHeight / 2;
        
        lines.forEach((line, lineIndex) => {
          this.ctx.fillText(line, textRadius, startY + lineIndex * lineHeight);
        });
        
        this.ctx.restore();
      });
      
      // Draw center circle
      this.ctx.beginPath();
      this.ctx.arc(centerX, centerY, 20, 0, 2 * Math.PI);
      this.ctx.fillStyle = '#ffffff';
      this.ctx.fill();
      this.ctx.strokeStyle = '#cccccc';
      this.ctx.lineWidth = 2;
      this.ctx.stroke();
    }
    
    bindEvents() {
      // Close popup
      this.closeBtn.addEventListener('click', () => {
        this.hidePopup();
        if (this.hasSpunToday()) {
          this.showMinimized();
        }
      });
      
      // Show popup from minimized
      this.minimized.addEventListener('click', () => {
        this.hideMinimized();
        this.showPopup();
        
        // If user has spun today, show the result
        if (this.hasSpunToday()) {
          const storedResult = this.getStoredResult();
          if (storedResult) {
            this.displayStoredResult(storedResult);
          }
        } else {
          // Reset to fresh form view if they haven't spun today
          this.resetFormView();
        }
      });
      
      // Form submission
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleSpin();
      });
      
      // Copy button
      if (this.copyButton) {
        this.copyButton.addEventListener('click', () => {
          this.copyCouponCode();
        });
      }
      
      // Close on overlay click
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.hidePopup();
          if (this.hasSpunToday()) {
            this.showMinimized();
          }
        }
      });
    }
    
    showPopup() {
      this.overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
    
    hidePopup() {
      this.overlay.classList.remove('active');
      document.body.style.overflow = '';
    }
    
    showMinimized() {
      this.minimized.classList.remove('hidden');
    }
    
    hideMinimized() {
      this.minimized.classList.add('hidden');
    }
    
    hasSpunToday() {
      const today = new Date().toDateString();
      const lastSpin = localStorage.getItem('luckyWheelLastSpin');
      return lastSpin === today;
    }
    
    markSpinToday() {
      const today = new Date().toDateString();
      localStorage.setItem('luckyWheelLastSpin', today);
    }
    
    storeResult(segment) {
      const today = new Date().toDateString();
      const result = {
        date: today,
        segment: segment,
        email: this.emailInput.value
      };
      localStorage.setItem('luckyWheelResult', JSON.stringify(result));
    }
    
    getStoredResult() {
      const today = new Date().toDateString();
      const stored = localStorage.getItem('luckyWheelResult');
      if (stored) {
        try {
          const result = JSON.parse(stored);
          if (result.date === today) {
            return result;
          }
        } catch (e) {
          console.error('Failed to parse stored result:', e);
        }
      }
      return null;
    }
    
    displayStoredResult(storedResult) {
      // Switch to result-only mode
      this.switchToResultOnlyMode();
      
      // Show the stored result
      this.showResult(storedResult.segment);
    }
    
    resetFormView() {
      // Reset to normal mode with form and wheel
      this.popup.classList.remove('result-only');
      this.content.classList.remove('result-only');
      
      // Reset form to initial state
      this.form.classList.remove('hidden');
      this.resultDiv.classList.remove('show');
      this.emailInput.disabled = false;
      this.spinButton.style.display = 'block';
      
      // Show header again
      this.element.querySelector('.tpt-lucky-wheel_header').classList.remove('hidden');
      
      // Remove daily limit message if exists
      const message = this.form.querySelector('.daily-limit-message');
      if (message) {
        message.remove();
      }
    }
    
    switchToResultOnlyMode() {
      // Add result-only classes to popup and content
      this.popup.classList.add('result-only');
      this.content.classList.add('result-only');
      
      // Hide form and header
      this.form.classList.add('hidden');
      this.element.querySelector('.tpt-lucky-wheel_header').classList.add('hidden');
      
      // Show result
      this.resultDiv.classList.add('show');
    }
    
    handleSpin() {
      // Check if already spun today
      if (this.hasSpunToday()) {
        alert('You can only spin once per day. Come back tomorrow!');
        return;
      }
      
      // Validate email
      if (!this.emailInput.value || !this.isValidEmail(this.emailInput.value)) {
        alert('Please enter a valid email address.');
        return;
      }
      
      // Check if already spinning
      if (this.isSpinning) return;
      
      this.spin();
    }
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }
    
    spin() {
      if (!this.wheelData.length) return;
      
      this.isSpinning = true;
      this.spinButton.disabled = true;
      this.spinButton.querySelector('span').textContent = 'Spinning...';
      
      // Random result
      const winningIndex = Math.floor(Math.random() * this.wheelData.length);
      const winningSegment = this.wheelData[winningIndex];
      
      // Calculate rotation to point to center of winning segment
      const segmentAngle = (2 * Math.PI) / this.wheelData.length;
      
      // The pointer points upward (at 270 degrees or -Math.PI/2)
      // We want the center of the winning segment to align with the pointer
      const targetAngle = (winningIndex * segmentAngle) + (segmentAngle / 2);
      
      // Calculate how much to rotate to align the segment center with the pointer
      // We need to rotate so that the target angle ends up at the pointer position (top)
      const pointerAngle = -Math.PI / 2; // Pointer is at top (270 degrees)
      const rotationNeeded = pointerAngle - targetAngle;
      
      // Add multiple full rotations for visual effect
      const spins = 5; // Number of full rotations
      const finalRotation = this.currentRotation + (spins * 2 * Math.PI) + rotationNeeded;
      
      // Animate spinning
      this.animateWheel(finalRotation, 3000, () => {
        // Show result immediately after spinning stops
        this.showResult(winningSegment);
        this.storeResult(winningSegment);
        this.markSpinToday();
        this.isSpinning = false;
        
        // Switch to result-only mode immediately
        this.switchToResultOnlyMode();
      });
    }
    
    animateWheel(targetRotation, duration, callback) {
      const startTime = Date.now();
      const startRotation = this.currentRotation;
      const rotationDifference = targetRotation - startRotation;
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (ease-out)
        const easeOut = 1 - Math.pow(1 - progress, 3);
        
        this.currentRotation = startRotation + rotationDifference * easeOut;
        this.drawWheel();
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Normalize rotation to prevent excessive values
          this.currentRotation = this.currentRotation % (2 * Math.PI);
          this.drawWheel();
          callback();
        }
      };
      
      animate();
    }
    
    showResult(segment) {
      this.resultTitle.textContent = segment.thankYouHeading;
      this.resultText.textContent = segment.thankYouText;
      
      if (segment.couponCode && segment.couponCode.trim() !== '') {
        this.couponCode.textContent = segment.couponCode;
        this.resultCoupon.classList.remove('hidden');
      } else {
        this.resultCoupon.classList.add('hidden');
      }
      
      // Show result
      this.resultDiv.classList.add('show');
      
      // Reset button state
      setTimeout(() => {
        this.spinButton.disabled = false;
        this.spinButton.querySelector('span').textContent = this.originalButtonText;
      }, 1000);
    }
    
    copyCouponCode() {
      const code = this.couponCode.textContent;
      if (!code) return;
      
      // Try to copy to clipboard
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(() => {
          this.showCopySuccess();
        }).catch(() => {
          this.fallbackCopyTextToClipboard(code);
        });
      } else {
        this.fallbackCopyTextToClipboard(code);
      }
    }
    
    fallbackCopyTextToClipboard(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        this.showCopySuccess();
      } catch (err) {
        console.error('Failed to copy: ', err);
        alert('Failed to copy coupon code. Please copy it manually: ' + text);
      }
      
      document.body.removeChild(textArea);
    }
    
    showCopySuccess() {
      const originalContent = this.copyButton.innerHTML;
      this.copyButton.classList.add('copied');
      this.copyButton.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <polyline points="20,6 9,17 4,12" stroke="currentColor" stroke-width="2" fill="none"/>
        </svg>
      `;
      
      setTimeout(() => {
        this.copyButton.classList.remove('copied');
        this.copyButton.innerHTML = originalContent;
      }, 2000);
    }
  }
  
  // Handle resize
  window.addEventListener('resize', () => {
    const luckyWheelElements = document.querySelectorAll('.tpt-lucky-wheel');
    luckyWheelElements.forEach(element => {
      const instance = element.luckyWheelInstance;
      if (instance) {
        instance.updateCanvasSize();
        instance.drawWheel();
      }
    });
  });
  
  // Store instances for resize handling
  document.addEventListener('DOMContentLoaded', () => {
    const luckyWheelElements = document.querySelectorAll('.tpt-lucky-wheel');
    luckyWheelElements.forEach(element => {
      const instance = new LuckyWheel(element);
      element.luckyWheelInstance = instance;
    });
  });